import { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

const contactName = (c) =>
  (c.company_name && c.company_name.trim())
    ? c.company_name
    : `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email || c.phone || c.id;

/** Searchable contact/company picker. Used to link a contractor portal user to a contact. */
export function ContactCombobox({
  contacts = [],
  value,
  onChange,
  placeholder = 'Select contact...',
  testId = 'contact-combobox',
}) {
  const [open, setOpen] = useState(false);
  const selected = contacts.find((c) => c.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={testId}
          className="w-full h-10 justify-between font-normal"
        >
          {selected ? (
            <span className="truncate">{contactName(selected)}</span>
          ) : (
            <span className="text-gray-400">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command
          filter={(value, search) => (value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0)}
        >
          <CommandInput placeholder="Search contact..." data-testid={`${testId}-input`} />
          <CommandList>
            <CommandEmpty>No contact found.</CommandEmpty>
            <CommandGroup>
              {contacts.map((c) => (
                <CommandItem
                  key={c.id}
                  value={`${c.company_name || ''} ${c.first_name || ''} ${c.last_name || ''} ${contactName(c)} ${c.email || ''} ${c.phone || ''} ${c.id}`}
                  data-testid={`${testId}-option-${c.id}`}
                  onSelect={() => { onChange(c.id); setOpen(false); }}
                >
                  <Check className={cn('mr-2 h-4 w-4', value === c.id ? 'opacity-100' : 'opacity-0')} />
                  <span className="truncate">{contactName(c)}</span>
                  {c.email && <span className="ml-2 text-xs text-gray-400 truncate">{c.email}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default ContactCombobox;
